import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Screens() {
  const [screens, setScreens] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    position: 1,
    type: 'standalone',
    group_id: '',
    resolution: '1080x1920'
  });

  useEffect(() => {
    fetchScreens();
  }, []);

  const fetchScreens = async () => {
    try {
      const response = await axios.get(`${API_URL}/screens`);
      setScreens(response.data);
    } catch (error) {
      toast.error('Nie udało się pobrać ekranów');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingScreen) {
        await axios.put(`${API_URL}/screens/${editingScreen.id}`, formData);
        toast.success('Ekran zaktualizowany');
      } else {
        await axios.post(`${API_URL}/screens`, formData);
        toast.success('Ekran dodany');
      }
      
      setOpen(false);
      resetForm();
      fetchScreens();
    } catch (error) {
      toast.error('Błąd podczas zapisywania');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten ekran?')) return;
    
    try {
      await axios.delete(`${API_URL}/screens/${id}`);
      toast.success('Ekran usunięty');
      fetchScreens();
    } catch (error) {
      toast.error('Nie udało się usunąć ekranu');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      position: 1,
      type: 'standalone',
      group_id: '',
      resolution: '1080x1920'
    });
    setEditingScreen(null);
  };

  const openEdit = (screen) => {
    setEditingScreen(screen);
    setFormData({
      name: screen.name,
      position: screen.position,
      type: screen.type,
      group_id: screen.group_id || '',
      resolution: screen.resolution
    });
    setOpen(true);
  };

  const copyDisplayUrl = (screen) => {
    const baseUrl = window.location.origin;
    const url = screen.type === 'connected' && screen.group_id 
      ? `${baseUrl}/display/group/${screen.group_id}` 
      : `${baseUrl}/display/${screen.id}`;
    
    navigator.clipboard.writeText(url).then(() => {
      toast.success('URL skopiowany do schowka');
    }).catch(() => {
      toast.error('Nie udało się skopiować URL');
    });
  };

  const isAdmin = user?.role === 'admin';

  return (
    <Layout>
      <div data-testid="screens-container" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Ekrany</h1>
            <p className="text-zinc-400">Zarządzaj wyświetlaczami w systemie</p>
          </div>
          {isAdmin && (
            <Dialog open={open} onOpenChange={(val) => {
              setOpen(val);
              if (!val) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-screen-button" className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Dodaj ekran
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
                <DialogHeader>
                  <DialogTitle>{editingScreen ? 'Edytuj ekran' : 'Nowy ekran'}</DialogTitle>
                  <DialogDescription className="text-zinc-400">
                    {editingScreen ? 'Zaktualizuj informacje o ekranie' : 'Dodaj nowy ekran do systemu'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Nazwa</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label htmlFor="position">Pozycja</Label>
                    <Input
                      id="position"
                      type="number"
                      value={formData.position}
                      onChange={(e) => setFormData({...formData, position: parseInt(e.target.value)})}
                      required
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label htmlFor="type">Typ</Label>
                    <Select value={formData.type} onValueChange={(val) => setFormData({...formData, type: val})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="connected">Połączony</SelectItem>
                        <SelectItem value="standalone">Samodzielny</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.type === 'connected' && (
                    <div>
                      <Label htmlFor="group_id">ID Grupy</Label>
                      <Input
                        id="group_id"
                        value={formData.group_id}
                        onChange={(e) => setFormData({...formData, group_id: e.target.value})}
                        placeholder="group-1"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="resolution">Rozdzielczość</Label>
                    <Input
                      id="resolution"
                      value={formData.resolution}
                      onChange={(e) => setFormData({...formData, resolution: e.target.value})}
                      placeholder="np. 1080x1920, 2160x3840"
                      required
                      className="bg-zinc-800 border-zinc-700"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Popularne: Full HD (1080x1920), 4K UHD (2160x3840), 8K (4320x7680)
                    </p>
                  </div>
                  <Button type="submit" className="w-full btn-primary">
                    {editingScreen ? 'Zaktualizuj' : 'Dodaj'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {screens.map((screen) => (
            <Card key={screen.id} data-testid={`screen-card-${screen.id}`} className="bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-white">{screen.name}</CardTitle>
                    <CardDescription className="text-zinc-400 mt-1">
                      Pozycja {screen.position} • {screen.type === 'connected' ? 'Połączony' : 'Samodzielny'}
                    </CardDescription>
                  </div>
                  <div className={`status-badge px-2 py-1 text-xs rounded-full ${
                    screen.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'
                  }`}>
                    {screen.status === 'active' ? 'Aktywny' : 'Nieaktywny'}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-zinc-400 mb-4">
                  <p>Rozdzielczość: {screen.resolution}</p>
                  {screen.group_id && <p>Grupa: {screen.group_id}</p>}
                  <p className="text-xs text-zinc-500 font-mono">ID: {screen.id}</p>
                </div>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <a
                      href={screen.type === 'connected' && screen.group_id 
                        ? `/display/group/${screen.group_id}` 
                        : `/display/${screen.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center px-3 py-2 text-sm bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg border border-blue-500/30 hover:border-blue-500/50 transition-all"
                    >
                      🖥️ Otwórz
                    </a>
                    <Button
                      onClick={() => copyDisplayUrl(screen)}
                      variant="outline"
                      size="sm"
                      className="border-zinc-700 hover:bg-zinc-800"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  {isAdmin && (
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => openEdit(screen)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-zinc-700 hover:bg-zinc-800"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edytuj
                      </Button>
                      <Button
                        onClick={() => handleDelete(screen.id)}
                        variant="outline"
                        size="sm"
                        className="border-red-900 text-red-400 hover:bg-red-900/20"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Usuń
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {screens.length === 0 && (
          <Card className="bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
            <CardContent className="py-12 text-center">
              <p className="text-zinc-500 mb-4">Brak ekranów w systemie</p>
              {isAdmin && <p className="text-zinc-600 text-sm">Kliknij "Dodaj ekran" aby rozpocząć</p>}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}